import { useLoadingStates } from 'hooks/use-loading.hook';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useAccountStore, useAuthStore, useDeviceStore } from 'store';
import { notification, RangeDatetimePickerFormItem } from 'ui-lib';
import { ReportComponent } from './report-component';
import { isNullOrEmpty } from 'common-utils';
import reportsCss from './reports.css';
import { TableUtils } from 'pages/private/components/table-utils';
import { ReportTable } from 'pages/private/components/report-table';

interface ICommissionedHistoryEntry {
    accountUuid: string;
    accountName: string;
    numberOfDevices: number;
}
export const CommissionedHistoryReportComponent = observer(() => {
    const [dataSource, setDataSource] = React.useState<ICommissionedHistoryEntry[]>([]);
    const devicesStore = useDeviceStore();
    const { errorMessages, loading, withLoading } = useLoadingStates();
    const { currentUser } = useAuthStore();
    const accountStore = useAccountStore();
    const onGenerateReport = async (values) => {
        const dateRange: moment.Moment[] = values['date-range'];
        if (isNullOrEmpty(dateRange)) {
            notification.error({
                message: 'Error',
                description: 'Please select a date range',
            });
            return;
        }
        withLoading(async () => {
            const data = await devicesStore.getCommissionedDevicesHistory(currentUser.accountId, {
                from: dateRange[0].toDate(),
                to: dateRange[1].toDate(),
            });
            const result = Object.keys(data).map((key) => ({
                accountUuid: key,
                accountName: accountStore.getAccountByUuid(key)?.name,
                numberOfDevices: data[key],
            }));
            setDataSource(result);
        });
    };

    return (
        <ReportComponent
            code='commissioned-history-report'
            isLoading={false}
            errorMessages={errorMessages}
            generateReportOptions={{
                isGenerating: loading,
                onGenerateReport,
            }}
            items={[
                <RangeDatetimePickerFormItem
                    code='date-range'
                    label='Date range'
                    labelAlign='right'
                    colon={true}
                    disabledDate={(values) => {
                        // As we don't have data before 2024, we can't calculate the count of list commissioned devices
                        return values.year() < 2024;
                    }}
                />,
            ]}
            reportTable={
                <ReportTable
                    title=''
                    loadingState={loading}
                    table={{
                        rowKey: 'accountUuid',
                        columns: [
                            TableUtils.createBasicFilterColumn({
                                key: 'accountName',
                                title: 'Account',
                            }),
                            TableUtils.createBasicFilterColumn({
                                key: 'numberOfDevices',
                                title: 'Number of devices',
                            }),
                        ],
                        dataSource: dataSource,
                    }}
                />
            }
        />
    );
});
